import React, {useEffect, useState} from 'react';

import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {MIME_TYPES} from '@mantine/dropzone';
import {IconCloudDownload} from '@tabler/icons-react';
import {ContentModal} from 'src/design/molecules/ContentModal';
import {Button} from 'src/design/atoms/Button';
import {DialogButton} from 'src/design/molecules/DialogButton';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {emptySplitApi} from 'src/redux/api';
import {useAppDispatch} from 'src/redux/hooks';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import ApiCaller, {ApiCallerDownloadFile} from 'src/shared/utils/ApiCaller';
import {isValidArray} from 'src/utilities/objectUtils';
import type {IFormFieldV2} from 'src/types/FormV2';

const fieldData: IFormFieldV2 = {
  name: 'vendorImport',
  label: 'Vendor File Upload',
  type: 'file',
  description:
    'Use the file upload below to select a .CSV file containing your vendor data. The data file must match the import template specification. You may download a copy of the template to match with your data using the "Download Template" button below.',
  fileUploadOptions: {
    allowedFileTypes: [MIME_TYPES.csv],
    isHideUploadSuccessEnabled: true,
  },
};

export interface VendorImportModalProps {
  show: boolean;
  onClose: any;
  onImportSuccess: any;
}

export const VendorImportModal: React.FC<VendorImportModalProps> = ({
  show,
  onClose,
  onImportSuccess,
}) => {
  const {setAlert} = useAlert();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [importedFiles, setImportedFiles] = useState<IFormDocumentUploadFile[]>(
    []
  );

  useEffect(() => {
    if (show) {
      handleOpenDialog();
    }
  }, [show]);

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    onClose();
  };

  const handleCancelDialog = () => {
    setImportedFiles(prevState => prevState);
    setOpen(false);
    onClose();
  };

  const handleUpload = (
    _name: string,
    _value: string,
    uploadedFiles: IFormDocumentUploadFile[]
  ) => {
    setImportedFiles(uploadedFiles);
  };

  const handleConfirmImport = () => {
    setIsSaving(true);

    if (isValidArray(importedFiles)) {
      importedFiles.forEach((file, index) => {
        ApiCaller({
          method: 'POST',
          endpointURL: `${ApiEndpoints.vendorImport}/${file.encodedKey}`,
        })
          .then(() => {
            if (index + 1 === importedFiles.length) {
              setIsSaving(false);
              dispatch(emptySplitApi.util.invalidateTags(['Vendors']));
              onImportSuccess();
            }
          })
          .catch(err => {
            setAlert(
              err.response?.data.message || 'error downloading the file',
              'error'
            );
            setIsSaving(false);
          });
      });
    }
  };

  const handleDownloadImportTemplate = () => {
    ApiCallerDownloadFile(
      ApiEndpoints.vendorImportTemplate,
      'vendor_import_template.csv'
    ).catch((error: unknown) => setAlert(parseErrorMessage(error), 'error'));
  };
  return (
    <div aria-label="manage-assignees-selectModal">
      <ContentModal
        title="Import Vendors"
        open={open}
        onCloseCallback={handleCloseDialog}
        actions={[
          <DialogButton
            key="template"
            onClick={handleDownloadImportTemplate}
            leftSection={<IconCloudDownload />}>
            Download Template
          </DialogButton>,
          <DialogButton key="cancel" onClick={handleCancelDialog}>
            Cancel
          </DialogButton>,
          <Button
            key="import"
            onClick={handleConfirmImport}
            loading={isSaving}
            disabled={!isValidArray(importedFiles)}
            aria-label="dialogOkBtn">
            {isSaving
              ? 'importing....'
              : `Import ${
                  importedFiles.length > 1
                    ? `${importedFiles.length}  Files`
                    : ` File`
                }`}
          </Button>,
        ]}>
        <Divider mb="md" />
        <Alert title="Warning!">
          The vendor import process only imports new vendors. You cannot modify
          existing vendors with this import process. Up to 1,000 records may be
          imported per file.
        </Alert>
        <Divider mt="md" mb="md" />
        <FormFileUploadV2
          field={fieldData}
          value={importedFiles}
          onChange={handleUpload}
        />
      </ContentModal>
    </div>
  );
};
